// BINGEPLAY-MCP-V1: "AI Connect" — where a dev generates a bearer
// token and copies a client-specific config snippet into Claude
// Desktop / ChatGPT / Cursor. Design system: matches the rest of the
// publisher pages (white card, #FF0050 primary, #FFE8EF hover chips).

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import McpService, { McpTokenStatus } from '@/services/McpService';

type ClientKind = 'claude' | 'chatgpt' | 'cursor';

const CLIENTS: Array<{
  id: ClientKind;
  label: string;
  blurb: string;
}> = [
  {
    id: 'claude',
    label: 'Claude',
    blurb:
      'Settings → Feature Preview → Connectors → Add custom connector. Paste the URL, then paste the token when Claude asks for auth.',
  },
  {
    id: 'chatgpt',
    label: 'ChatGPT',
    blurb:
      'Settings → Connectors → Add custom → paste the URL and the token. Available on Team & Enterprise plans.',
  },
  {
    id: 'cursor',
    label: 'Cursor',
    blurb:
      'Cursor Settings → Features → Model Context Protocol → Add server. Same URL + token pair.',
  },
];

const MCP_URL = 'https://api.funizzle.com/mcp';

const claudeSnippet = (token: string) => `URL:   ${MCP_URL}
Auth:  Bearer ${token || 'YOUR_TOKEN_HERE'}`;

const chatgptSnippet = (token: string) => `URL:   ${MCP_URL}
Auth:  Bearer ${token || 'YOUR_TOKEN_HERE'}`;

const cursorSnippet = (token: string) => `URL:   ${MCP_URL}
Auth:  Bearer ${token || 'YOUR_TOKEN_HERE'}`;

function snippetFor(client: ClientKind, token: string): string {
  if (client === 'claude') return claudeSnippet(token);
  if (client === 'chatgpt') return chatgptSnippet(token);
  return cursorSnippet(token);
}

const CopyButton: React.FC<{ value: string; label?: string }> = ({
  value,
  label = 'Copy',
}) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error('Clipboard blocked. Select and copy manually.');
        }
      }}
      className='px-3 py-1.5 rounded-md text-sm font-medium bg-[#FF0050] text-white hover:bg-[#e30047] transition-colors'
    >
      {copied ? 'Copied ✓' : label}
    </button>
  );
};

const AiConnect: React.FC = () => {
  const [client, setClient] = useState<ClientKind>('claude');
  const [status, setStatus] = useState<McpTokenStatus | null>(null);
  const [freshToken, setFreshToken] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void refreshStatus();
  }, []);

  async function refreshStatus() {
    try {
      const s = await McpService.status();
      setStatus(s);
    } catch (e: any) {
      toast.error(e?.message || 'Could not load MCP status');
    }
  }

  async function handleIssue() {
    setLoading(true);
    try {
      const res = await McpService.issue();
      setFreshToken(res.token);
      await refreshStatus();
      toast.success(
        'New token generated. Copy it now — it will not be shown again.',
      );
    } catch (e: any) {
      toast.error(e?.message || 'Token generation failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleRevoke() {
    if (
      !window.confirm(
        'Revoke your current token? Your Claude / ChatGPT connection will stop working until you generate + paste a new one.',
      )
    )
      return;
    setLoading(true);
    try {
      await McpService.revoke();
      setFreshToken('');
      await refreshStatus();
      toast.success('Token revoked.');
    } catch (e: any) {
      toast.error(e?.message || 'Revoke failed');
    } finally {
      setLoading(false);
    }
  }

  const connected = status?.connected ?? false;
  // Show token in snippet ONLY when the dev just generated one this
  // session — never persist it to state that survives a page reload.
  const tokenForSnippet = freshToken || (connected ? '••••••••••••••••' : '');
  const snippet = useMemo(
    () => snippetFor(client, tokenForSnippet),
    [client, tokenForSnippet],
  );
  const activeToken = status?.tokens?.[0];

  return (
    <div className='p-6 sm:p-8 max-w-[900px]'>
      {/* Hero */}
      <div className='mb-8'>
        <h1 className='text-[28px] font-semibold text-black leading-tight'>
          AI Connect
        </h1>
        <p className='text-[15px] text-[#69666A] mt-2 max-w-[640px]'>
          Skip the dev portal — talk to BingePlay from Claude Desktop, ChatGPT,
          or Cursor. Generate a token, paste the config, and Astra can list your
          gamics, check your stats, and pull attribution links on demand.
        </p>
      </div>

      {/* Status card */}
      <div className='bg-white border border-[#EDEDED] rounded-2xl p-6 mb-6 shadow-sm'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <span
              className={[
                'w-2.5 h-2.5 rounded-full',
                connected ? 'bg-green-500' : 'bg-gray-300',
              ].join(' ')}
            />
            <div>
              <div className='text-[16px] font-medium'>
                {connected ? 'Connected' : 'Not connected yet'}
              </div>
              {connected && activeToken && (
                <div className='text-[13px] text-[#69666A] mt-0.5'>
                  Token created{' '}
                  {new Date(activeToken.created_at).toLocaleDateString()}
                  {activeToken.last_used_at
                    ? ` · last used ${new Date(activeToken.last_used_at).toLocaleString()}`
                    : ' · not used yet'}
                </div>
              )}
            </div>
          </div>
          <div className='flex gap-2'>
            {connected && (
              <button
                onClick={handleRevoke}
                disabled={loading}
                className='px-4 py-2 rounded-md text-sm font-medium text-[#FF0050] border border-[#FF0050] hover:bg-[#FFE8EF] transition-colors disabled:opacity-50'
              >
                Revoke
              </button>
            )}
            <button
              onClick={handleIssue}
              disabled={loading}
              className='px-4 py-2 rounded-md text-sm font-medium bg-[#FF0050] text-white hover:bg-[#e30047] transition-colors disabled:opacity-50'
            >
              {connected ? 'Regenerate token' : 'Generate token'}
            </button>
          </div>
        </div>

        {freshToken && (
          <div className='mt-4 p-4 bg-[#FFF6F8] border border-[#FFD5E1] rounded-lg'>
            <div className='flex items-start justify-between gap-3'>
              <div>
                <div className='text-[13px] font-semibold text-[#FF0050]'>
                  Copy this token now
                </div>
                <div className='text-[12px] text-[#69666A] mt-0.5'>
                  This is the only time it will be shown. It's already baked
                  into the snippet below.
                </div>
              </div>
              <CopyButton value={freshToken} />
            </div>
            <div className='mt-2 font-mono text-[12px] break-all bg-white border border-[#EDEDED] rounded p-2'>
              {freshToken}
            </div>
          </div>
        )}
      </div>

      {/* Client tabs */}
      <div className='bg-white border border-[#EDEDED] rounded-2xl p-6 shadow-sm'>
        <div className='flex gap-2 mb-4 border-b border-[#EDEDED]'>
          {CLIENTS.map((c) => (
            <button
              key={c.id}
              onClick={() => setClient(c.id)}
              className={[
                'px-4 py-3 text-[14px] font-medium border-b-2 transition-colors -mb-px',
                client === c.id
                  ? 'border-[#FF0050] text-[#FF0050]'
                  : 'border-transparent text-[#69666A] hover:text-black',
              ].join(' ')}
            >
              {c.label}
            </button>
          ))}
        </div>

        <p className='text-[14px] text-[#69666A] mb-4'>
          {CLIENTS.find((c) => c.id === client)?.blurb}
        </p>

        <div className='relative'>
          <pre className='bg-[#0F1115] text-[#E4E7EB] rounded-lg p-4 pr-24 text-[12.5px] font-mono overflow-x-auto whitespace-pre-wrap break-all'>
            {snippet}
          </pre>
          <div className='absolute top-3 right-3'>
            <CopyButton value={snippet} label='Copy config' />
          </div>
        </div>

        <div className='mt-6 text-[13px] text-[#69666A]'>
          Once connected, try:
          <ul className='mt-2 space-y-1.5 list-disc list-inside'>
            <li>"How many taps did my gamics get today?"</li>
            <li>"Pull the BingePlay attribution link for [gamic name]."</li>
            <li>"List my gamics with their statuses."</li>
          </ul>
        </div>
      </div>

      {/* Security footer */}
      <p className='mt-6 text-[12px] text-[#909090]'>
        Your token grants read-only access to your own BingePlay data (profile,
        gamics, stats, attribution links). Revoke it any time above.
      </p>
    </div>
  );
};

export default AiConnect;
